import { useMemo, useState } from 'react';
import { useCan } from '@/hooks/use-scopes';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, Navigate } from 'react-router-dom';
import { Plus, RefreshCw, Copy, ShieldOff, ShieldCheck, Mail, Clock, Check, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { Card, PageHeader } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import { cn } from '@/lib/cn';

interface UserRow {
  id: string;
  email: string;
  display_name: string;
  photo_url?: string | null;
  role: string;
  designations: string[];
  active: boolean;
  last_seen_at?: string | null;
  invited_at?: string | null;
  invite_expires_at?: string | null;
  created_at: string;
}

interface InviteResponse {
  user: UserRow;
  invite_token: string;
  invite_url: string;
  expires_in_days: number;
}

type Filter = 'all' | 'active' | 'pending' | 'expired' | 'inactive';

export default function UsersPage() {
  const allowed = useCan('admin.panel');
  const qc = useQueryClient();
  const [filter, setFilter] = useState<Filter>('all');
  const [revealed, setRevealed] = useState<Record<string, InviteResponse>>({});

  const users = useQuery({
    queryKey: ['users'],
    queryFn: async () => (await api.get<UserRow[]>('/users')).data,
  });

  const reissue = useMutation({
    mutationFn: async (id: string) => (await api.post<InviteResponse>(`/users/${id}/reissue-invite`)).data,
    onSuccess: (data) => {
      setRevealed((cur) => ({ ...cur, [data.user.id]: data }));
      toast.success('New invite link generated');
      qc.invalidateQueries({ queryKey: ['users'] });
    },
  });

  const setActive = useMutation({
    mutationFn: async ({ id, active }: { id: string; active: boolean }) =>
      (await api.patch<UserRow>(`/users/${id}`, { active })).data,
    onSuccess: (_d, vars) => {
      toast.success(vars.active ? 'User reactivated' : 'User deactivated');
      qc.invalidateQueries({ queryKey: ['users'] });
    },
  });

  const filtered = useMemo(() => {
    const list = users.data ?? [];
    const now = Date.now();
    return list.filter((u) => {
      const seen = !!u.last_seen_at;
      const expiresAt = u.invite_expires_at ? new Date(u.invite_expires_at).getTime() : null;
      const expired = !seen && expiresAt !== null && expiresAt < now;
      const pending = !seen && expiresAt !== null && expiresAt >= now;
      switch (filter) {
        case 'active':   return seen && u.active;
        case 'pending':  return pending && u.active;
        case 'expired':  return expired && u.active;
        case 'inactive': return !u.active;
        default:         return true;
      }
    });
  }, [users.data, filter]);

  const counts = useMemo(() => {
    const list = users.data ?? [];
    const now = Date.now();
    let active = 0, pending = 0, expired = 0, inactive = 0;
    for (const u of list) {
      const seen = !!u.last_seen_at;
      const expiresAt = u.invite_expires_at ? new Date(u.invite_expires_at).getTime() : null;
      if (!u.active) inactive++;
      else if (seen) active++;
      else if (expiresAt !== null && expiresAt < now) expired++;
      else if (expiresAt !== null) pending++;
    }
    return { all: list.length, active, pending, expired, inactive };
  }, [users.data]);

  if (!allowed) return <Navigate to="/" replace />;

  async function copyLink(url: string) {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link copied');
    } catch {
      toast.error('Copy failed; select manually');
    }
  }

  return (
    <div>
      <PageHeader
        title="Team & access"
        subtitle="Invite teammates, track who has accepted, regenerate magic links if they expire."
        actions={
          <Link to="/admin/invite">
            <Button icon={<Plus size={14} />}>Invite teammate</Button>
          </Link>
        }
      />

      {/* Filter tabs */}
      <div className="flex flex-wrap gap-2 mb-5">
        {(['all', 'active', 'pending', 'expired', 'inactive'] as Filter[]).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={cn(
              'text-xs px-3 py-1.5 rounded-full border transition capitalize',
              filter === f
                ? 'border-accent-400 bg-accent-600/20 text-accent-400'
                : 'border-slate-800 text-muted hover:text-muted-strong'
            )}
          >
            {f} <span className="opacity-60">{counts[f]}</span>
          </button>
        ))}
      </div>

      {users.isLoading && <div className="text-muted text-sm">Loading...</div>}

      {!users.isLoading && filtered.length === 0 && (
        <Card>
          <div className="text-center py-8 space-y-3">
            <p className="text-muted text-sm">No users in this filter.</p>
          </div>
        </Card>
      )}

      <div className="space-y-3">
        {filtered.map((u) => {
          const status = computeStatus(u);
          const reissued = revealed[u.id];
          return (
            <Card key={u.id} className="space-y-3">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-center gap-3 min-w-0">
                  {u.photo_url ? (
                    <img src={u.photo_url} alt="" className="w-10 h-10 rounded-full object-cover" />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-accent-600/20 text-accent-400 flex items-center justify-center text-sm font-semibold">
                      {u.display_name.slice(0, 2).toUpperCase()}
                    </div>
                  )}
                  <div className="min-w-0">
                    <div className="font-medium text-muted-strong truncate">
                      {u.display_name}
                      <StatusBadge status={status} />
                    </div>
                    <div className="text-xs text-muted truncate">{u.email}</div>
                  </div>
                </div>

                <div className="text-right text-xs space-y-1 shrink-0">
                  <div className="text-accent-400 uppercase tracking-wide">{u.role}</div>
                  {u.designations.length > 0 && (
                    <div className="text-muted text-[10px]">{u.designations.join(', ').replace(/_/g, ' ')}</div>
                  )}
                </div>
              </div>

              {/* Status / expiry row */}
              <div className="flex flex-wrap gap-x-5 gap-y-1 text-[11px] text-muted pl-13">
                {u.last_seen_at ? (
                  <span className="inline-flex items-center gap-1.5"><Check size={11} className="text-emerald-400" /> Last seen {fmt(u.last_seen_at)}</span>
                ) : (
                  <span className="inline-flex items-center gap-1.5"><Mail size={11} /> Invited {fmt(u.invited_at)}</span>
                )}
                {!u.last_seen_at && u.invite_expires_at && (
                  <span className={cn(
                    'inline-flex items-center gap-1.5',
                    new Date(u.invite_expires_at).getTime() < Date.now() ? 'text-red-400' : 'text-yellow-400'
                  )}>
                    <Clock size={11} /> {expiryText(u.invite_expires_at)}
                  </span>
                )}
              </div>

              {/* Reissued link reveal */}
              {reissued && (
                <div className="rounded-lg border border-accent-400/40 bg-accent-600/10 p-3 space-y-2">
                  <div className="text-[11px] text-accent-400">New magic link (valid {reissued.expires_in_days} days)</div>
                  <div className="flex gap-2">
                    <input
                      readOnly
                      value={reissued.invite_url}
                      onClick={(e) => e.currentTarget.select()}
                      className="flex-1 h-9 rounded-lg border border-slate-800 bg-bg px-2 text-xs text-muted-strong font-mono"
                    />
                    <Button size="sm" icon={<Copy size={12} />} onClick={() => copyLink(reissued.invite_url)}>Copy</Button>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex flex-wrap gap-2 justify-end pt-2 border-t border-slate-800">
                <Link to={`/admin/users/${u.id}`}>
                  <Button size="sm" icon={<Pencil size={12} />}>Edit profile</Button>
                </Link>
                {!u.last_seen_at && u.active && (
                  <Button
                    size="sm" variant="secondary" icon={<RefreshCw size={12} />}
                    onClick={() => reissue.mutate(u.id)}
                    loading={reissue.isPending && reissue.variables === u.id}
                  >
                    {u.invite_expires_at && new Date(u.invite_expires_at).getTime() < Date.now() ? 'Regenerate (expired)' : 'Regenerate link'}
                  </Button>
                )}
                {u.active ? (
                  <Button
                    size="sm" variant="ghost" icon={<ShieldOff size={12} />}
                    onClick={() => setActive.mutate({ id: u.id, active: false })}
                    loading={setActive.isPending}
                  >Deactivate</Button>
                ) : (
                  <Button
                    size="sm" variant="secondary" icon={<ShieldCheck size={12} />}
                    onClick={() => setActive.mutate({ id: u.id, active: true })}
                    loading={setActive.isPending}
                  >Reactivate</Button>
                )}
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

type Status = 'active' | 'pending' | 'expired' | 'inactive';

function computeStatus(u: UserRow): Status {
  if (!u.active) return 'inactive';
  if (u.last_seen_at) return 'active';
  if (u.invite_expires_at && new Date(u.invite_expires_at).getTime() < Date.now()) return 'expired';
  return 'pending';
}

function StatusBadge({ status }: { status: Status }) {
  const map: Record<Status, string> = {
    active:   'bg-emerald-600/15 text-emerald-300 border-emerald-500/40',
    pending:  'bg-yellow-600/15 text-yellow-300 border-yellow-500/40',
    expired:  'bg-red-600/15 text-red-300 border-red-500/40',
    inactive: 'bg-slate-700/40 text-slate-400 border-slate-600/40',
  };
  return (
    <span className={cn('ml-2 inline-block px-2 py-0.5 rounded-full text-[9px] uppercase tracking-wide border', map[status])}>
      {status}
    </span>
  );
}

function fmt(iso?: string | null): string {
  if (!iso) return '-';
  return new Date(iso).toLocaleString('en-AE', { dateStyle: 'medium', timeStyle: 'short', timeZone: 'Asia/Dubai' });
}

function expiryText(iso: string): string {
  const now = Date.now();
  const ts = new Date(iso).getTime();
  const dayMs = 86_400_000;
  const diffDays = Math.round((ts - now) / dayMs);
  if (diffDays < 0) return `Expired ${Math.abs(diffDays)}d ago (${fmt(iso)})`;
  if (diffDays === 0) return `Expires today (${fmt(iso)})`;
  return `Expires in ${diffDays}d (${fmt(iso)})`;
}
